<script lang="ts">
  import { BREAKPOINTS } from '$lib/constants';
  import type { App } from '$lib/types';
  import type { Api_Game } from '$lib/types/api/graphql';
  import { pluralize } from '$lib/utils';
  import { createEventDispatcher, getContext } from 'svelte';
  import { clickOutsideAction } from '$lib/directives/clickOutsideAction';

  import FormControl from '$components/FormControl.svelte';
  import GameChip from './GameChip.svelte';
  import SearchDropDown from './SearchDropDown.svelte';

  import FontAwesomeIcon from '$components/FontAwesomeIcon.svelte';
  import LoadingSpinner from '$mds/LoadingSpinner';
  import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-solid-svg-icons';

  const { innerWidth } = getContext<App>('app');

  const dispatch = createEventDispatcher();

  export let totalCount = null;
  export let query = '';
  export let previousQuery = '';
  export let results = {};
  export let isFetching = false;
  export let isSearchPage = false;
  export let isSearched = false;
  export let inputFocus;
  export let dropdownFocus;
  export let currentGame: Api_Game;
  export let placeholder = 'Search for Games, Communities, and more...';
  export let isFloating = true;
  export let lightGameHover = false;
  export let hasSeeAll = true;
  export let isFocused = false;
  // current selected index for filtering
  export let selectedFilter = 'all';
  export let alwaysShowClearButton = false;

  // For binding with client width
  let gameChipWidth = 0;

  $: if (!currentGame) gameChipWidth = 0;

  export let showFilteredResults = false;
  let className = '';
  export { className as class };

  let elements = [];

  const handleClickOutside = () => {
    dispatch('close');
    isSearched = false;
  };

  function handleKeydown(e) {
    if (isSearching) {
      if (e.key === 'Backspace' && query?.length < 1 && currentGame) {
        dispatch('removeGame');
        gameChipWidth = 0;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        dropdownFocus?.();
      }
      if (e.key === 'Escape') {
        dispatch('close');
        e.preventDefault();
        isSearched = false;
      }
    }
  }

  $: isSearching = showFilteredResults || query.length > 0 || isSearched;
  $: isMobile = $innerWidth < BREAKPOINTS.md;
</script>

<div
  use:clickOutsideAction
  on:clickoutside={isFloating && handleClickOutside()}
  class="relative {className} flex max-h-[100svh] flex-col gap-3 py-2"
>
  <div class="mx-auto flex h-16 w-full items-center md:w-[560px]">
    <form class="group relative flex-1" on:submit|preventDefault={() => isSearchPage && dispatch('submit')}>
      <div
        class="left-4.5 absolute top-1/2 flex -translate-y-1/2 flex-row {isSearching
          ? 'text-ondark-primary'
          : 'text-ondark-subtle'}"
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} class="w-4" />
        {#if currentGame}
          <div class="hidden pl-4 sm:block" bind:clientWidth={gameChipWidth}>
            <GameChip
              game={currentGame}
              on:removeGame
              backgroundColor="bg-ondark-medium hover:bg-ondark-high group-hover:bg-ondark-high group-focus-within:bg-ondark-high"
            />
          </div>
        {/if}
      </div>

      <FormControl
        type="text"
        {placeholder}
        spellcheck={false}
        inlinePadding={52 + (isMobile ? 0 : gameChipWidth)}
        inlinePaddingDirection="left"
        class="sm:bg-ondark-low sm:hover:bg-ondark-medium sm:focus:bg-ondark-medium !h-12 rounded-md text-base sm:hover:border-transparent sm:focus:border-transparent"
        bind:value={query}
        on:enter
        on:blur
        on:focus
        on:input={({ target }) => {
          elements = [];
          query = target.value;
          dispatch('search');
        }}
        on:keydown={handleKeydown}
        bind:focus={inputFocus}
        {isFocused}
      />

      {#if totalCount && !!query}
        <span class="right-5.5 text-ondark-secondary absolute top-1/2 -translate-y-1/2">
          {pluralize('result', totalCount)}
        </span>
      {/if}

      <button
        class="bg-ondark-low absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md {(isSearchPage ||
          !query) &&
        !alwaysShowClearButton
          ? 'hidden'
          : ''}"
        on:click={() => {
          query = '';
          dispatch('close');
        }}
      >
        <FontAwesomeIcon icon={faXmark} class="w-3 " />
        <span
          class="bg-ondark-medium absolute inset-0 flex items-center justify-center {isFetching
            ? ''
            : 'opacity-0'} duration-400 transition-opacity"
        >
          <LoadingSpinner />
        </span>
      </button>
    </form>
  </div>

  {#if currentGame && isMobile}
    <div class="mt-4 flex sm:hidden">
      <GameChip backgroundColor="bg-ondark-low" game={currentGame} on:removeGame />
    </div>
  {/if}

  {#if !showFilteredResults}
    <div
      class="{isFloating
        ? 'bg-ondark-low md:bg-ondark-low md:border-ondark-subtle mx-auto w-full shrink grow-0 overflow-y-scroll rounded-lg px-3 md:max-w-[980px] md:overflow-visible md:border md:px-6'
        : ''} relative"
    >
      <SearchDropDown
        {isFetching}
        {query}
        {previousQuery}
        {currentGame}
        {results}
        {lightGameHover}
        {hasSeeAll}
        bind:elements
        bind:focus={dropdownFocus}
        bind:selectedFilter
        on:searchInputFocus={focus?.()}
        on:clickAll
        on:filterByIndex
        on:item:click={() => {
          dispatch('close');
          isSearched = false;
        }}
      />
    </div>
  {/if}
</div>
